/**
 * Wire → view mapping for interactive agent messages.
 *
 * The backend stores both interactive shapes under the same envelope
 * (`content.kind === 'interactive'`), and only `content.data.type` tells
 * them apart — see mirlo-messages-service `agent-client.service.ts`
 * (`mapMessageToWhatsappContent`):
 *
 *   - `button` — a flow's `send_message` carrying `buttons`. Up to 3 on
 *     WhatsApp, up to 10 on webchat.
 *   - `list`   — a flow's `send_list` node. On WhatsApp the `action.button`
 *     label opens a sheet grouping rows into sections; the widget has no
 *     sheet, so the rows render as the same stacked options a `button`
 *     payload produces, and the label itself is dropped (the options are
 *     already on screen, so there is nothing left for it to open).
 *
 * Both collapse to one flat list of options because clicking any of them
 * does the same thing: send the option's `title` as a plain user message,
 * which is what the flow's `menu` / `wait_for_reply` compares against.
 *
 * Lives apart from `useChatState` because the same mapping runs on two
 * paths — the live `agent_message` socket event and the transcript the
 * history endpoint replays on reload. Same reason `toMediaAttachment`
 * sits in `client/media.ts`.
 */

import type { InteractiveData } from '../client/api-types';

/**
 * Maximum number of options rendered per interactive message.
 *
 * For lists this matches what the backend already enforces (WhatsApp caps
 * a list at 10 rows TOTAL across all sections, and `agent-client.service.ts`
 * slices to that before persisting). For buttons it is a defensive ceiling
 * rather than a platform limit — webchat is our own channel and renders
 * more than WhatsApp's 3 — kept bounded so a misbehaving flow cannot dump
 * an unbounded stack into the panel.
 */
export const MAX_INTERACTIVE_OPTIONS = 10;

/**
 * One clickable option parsed off an interactive agent message. `id` is
 * the flow-defined button/row id; `title` is the human-readable label the
 * widget renders AND the value it sends back when the visitor clicks —
 * matching WhatsApp semantics, where the flow compares against the
 * visible title.
 */
export interface ReplyButtonOption {
  id: string;
  title: string;
  /**
   * Secondary line under the title. Only list rows carry one (WhatsApp
   * caps it at 72 chars); button payloads have no equivalent field.
   */
  description?: string;
  /**
   * Section heading rendered above this option. Set only on the first
   * option of a section, and only when the message actually splits its
   * options across more than one titled section — a single-section list
   * (the common case) renders as a plain stack, identical to buttons.
   */
  sectionTitle?: string;
}

/** View-model fields an interactive payload contributes to a bubble. */
export interface ParsedInteractive {
  replyButtons?: ReplyButtonOption[];
  /** List header, rendered as a bold line above the body. Lists only. */
  header?: string;
  /** Subtle line under the body. Both shapes may carry one. */
  footer?: string;
}

/** Narrow wire shape both the socket event and a history row satisfy. */
interface InteractiveEnvelope {
  kind?: string;
  data?: InteractiveData | null;
}

/**
 * Extracts the options, header and footer a bubble should render.
 *
 * Defensive by construction: anything malformed degrades to `{}` so the
 * caller falls back to a plain text bubble rather than throwing. A
 * payload that parses to zero valid options omits `replyButtons`
 * entirely, which is what the render path treats as "nothing to show".
 */
export function parseInteractive(
  content?: InteractiveEnvelope | null,
): ParsedInteractive {
  if (!content || content.kind !== 'interactive' || !content.data) return {};
  const data = content.data;

  if (data.type === 'button') {
    const buttons = Array.isArray(data.action?.buttons)
      ? data.action.buttons
      : [];
    const replyButtons = buttons
      .filter((b) => b?.type === 'reply' && b.reply?.id && b.reply?.title)
      .slice(0, MAX_INTERACTIVE_OPTIONS)
      .map((b) => ({ id: b.reply.id, title: b.reply.title }));

    return {
      ...(replyButtons.length > 0 ? { replyButtons } : {}),
      ...(data.footer?.text ? { footer: data.footer.text } : {}),
    };
  }

  if (data.type === 'list') {
    const sections = Array.isArray(data.action?.sections)
      ? data.action.sections
      : [];

    // Section headings only earn their keep when they actually separate
    // groups. One section (or untitled ones) would render a lone label
    // over the whole stack, which reads as noise rather than structure.
    const titledSections = sections.filter(
      (s) => Array.isArray(s?.rows) && s.rows.length > 0 && s?.title,
    );
    const showSectionTitles = titledSections.length > 1;

    const replyButtons: ReplyButtonOption[] = [];
    for (const section of sections) {
      if (replyButtons.length >= MAX_INTERACTIVE_OPTIONS) break;
      const rows = Array.isArray(section?.rows) ? section.rows : [];
      let firstOfSection = true;

      for (const row of rows) {
        if (replyButtons.length >= MAX_INTERACTIVE_OPTIONS) break;
        if (!row?.id || !row?.title) continue;

        replyButtons.push({
          id: row.id,
          title: row.title,
          ...(row.description ? { description: row.description } : {}),
          ...(showSectionTitles && firstOfSection && section.title
            ? { sectionTitle: section.title }
            : {}),
        });
        firstOfSection = false;
      }
    }

    return {
      ...(replyButtons.length > 0 ? { replyButtons } : {}),
      ...(data.header?.text ? { header: data.header.text } : {}),
      ...(data.footer?.text ? { footer: data.footer.text } : {}),
    };
  }

  return {};
}
